import { FormNames } from "./CESTypes";
import CESAppInfo from "./CESAppInfo";

export interface CESConfigurationOptions {
  apiBaseUrl: string;
  cdnBaseUrl: string;
  clientId: string;
  locale: string;
}

export class CESConfiguration {
  readonly apiBaseUrl: string;
  readonly cdnBaseUrl: string;
  readonly clientId: string;
  readonly locale: string;

  constructor(options: CESConfigurationOptions) {
    if (!options.apiBaseUrl) {
      throw new Error("CESConfiguration: apiBaseUrl is required");
    }
    if (!options.cdnBaseUrl) {
      throw new Error("CESConfiguration: cdnBaseUrl is required");
    }

    this.apiBaseUrl = options.apiBaseUrl.replace(/\/+$/, "");
    this.cdnBaseUrl = options.cdnBaseUrl.replace(/\/+$/, "");
    this.clientId = options.clientId;
    this.locale = options.locale;
  }

  getEndpointUrl(path: string): string {
    return `${this.apiBaseUrl}${path}`;
  }

  getCdnUrl(fileName: string): string {
    return `${this.cdnBaseUrl}/${this.clientId}/${fileName}`;
  }
}

const getGenericConfig = (): any => ({
  host: CESAppInfo.host,
  endpoint: CESAppInfo.endpoint,
  ciamClientId: CESAppInfo.ciamClientId
});

export const getProfileFormConfig = (queryString: any) => {
  const config = getGenericConfig();

  config.endpoint = `${config.endpoint}/consumer/profileform?${queryString}`;
  config.method = "GET";

  return config;
}

export const getFormConfig = (queryString: any) => {
  const config = getGenericConfig();

  config.endpoint = `${config.endpoint}/consumer/forms?${queryString}`;
  config.method = "GET";

  return config;
}

export const editProfileConfig = (json: string) => {
  const config = getGenericConfig();

  config.endpoint = `${config.endpoint}/consumer`;
  config.method = "PUT";
  config.body = json;

  return config;
}

export const submitFormConfig = (json: string, formType: string) => {
  const config = getGenericConfig();

  switch (formType) {
    case FormNames.Registration:
      config.endpoint = `${config.endpoint}/consumer/registration`;
      config.method = "POST";
      break;
    case FormNames.SignIn:
      config.endpoint = `${config.endpoint}/consumer/authentication`;
      config.method = "POST";
      break;
    case FormNames.PostLogin:
      config.endpoint = `${config.endpoint}/consumer/postLogin`;
      config.method = "PUT";
      break;
    case FormNames.ChangePassword:
      config.endpoint = `${config.endpoint}/consumer/password`;
      config.method = "PUT";
      break;
    case FormNames.ForgotPassword:
      config.endpoint = `${config.endpoint}/consumer/password/forgot`;
      config.method = "POST";
      break;
    case FormNames.ResetPassword:
      config.endpoint = `${config.endpoint}/consumer/password`;
      config.method = "POST";
      break;
    case FormNames.ResendVerification:
      config.endpoint = `${config.endpoint}/consumer/verificationcode/resend`;
      config.method = "POST";
      break;
    case FormNames.GenerateOtpForm:
      config.endpoint = `${config.endpoint}/otp/generateConsumerOtp`;
      config.method = "POST";
      break;
    case FormNames.ValidateOtpForm:
      config.endpoint = `${config.endpoint}/otp/validateConsumerOtp?clientId=${config.ciamClientId}`;
      config.method = "POST";
      break;
    case FormNames.ValidateEmailForm:
      config.endpoint = `${config.endpoint}/otp/validateEmail?clientId=${config.ciamClientId}`;
      config.method = "POST";
      break;
    default:
      config.endpoint = `${config.endpoint}/consumer`;
      config.method = "PUT";
      break;
  }

  config.body = json;

  return config;
}

export const loadExperienceDefinition = (experienceIdentifier: string) => {
  const config = getGenericConfig();

  config.endpoint = `${config.endpoint}/experience/${experienceIdentifier}`;
  config.method = "GET";

  return config;
}

/**
 * Get config for GET getUserInfo request
 */
export const getUserInfoConfig = () => {
  const config = getGenericConfig();

  config.endpoint = `${config.endpoint}/consumer`;
  config.method = "GET";

  return config;
}

/**
 * GET config for social native integration
 */
export const socialNative = (providerName: String, appId: String) => {
  const config = getGenericConfig();

  config.endpoint = `${config.endpoint}/social/${providerName}/init/${appId}`;
  config.method = "GET";

  return config;
}

/**
 * Get email verification configuration for requests
 * @param  {string} code
 */
export const checkEmailVerificaiton = (code: string) => {
  const config = getGenericConfig();

  config.endpoint = `${config.endpoint}/consumer/verificationcode`;
  config.body = JSON.stringify({ otp: code });
  config.method = "POST";

  return config;
}

export const logoutConfig = () => {
  const config = getGenericConfig();

  config.endpoint = `${config.endpoint}/consumer/token/invalidate`;
  config.method = "POST";

  return config;
}

export const accessTokenConfig = () => {
  const config = getGenericConfig();

  config.endpoint = `${config.endpoint}/token/renewal`;
  config.method = "POST";

  return config;
}
